import * as Tabs from "@radix-ui/react-tabs"
import { useAppSelector } from "@redux/app/hooks"
import { getUserData } from "@redux/features/userData/userDataSlice"
import { useQueryClient } from "@tanstack/react-query"
import { useLocation, useMatch, useNavigate } from "@tanstack/react-router"
import { HeaderLayout, LoadingCircle } from "@ui-lib/src"
import { common } from "@utils/common"
import { seempSocket } from "@utils/websocket/seempSocket"
import { seempSocket2 } from "@utils/websocket/seempSocket2"
import {
	AppStructure,
	Comment,
	PlantObject,
	SeempAction,
	SeempActionResult,
	SeempReport,
} from "api"
import { EventSourceManager } from "open-lib"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSeempGetActionsForVesselQuery } from "./queries/useSeempGetActionsForVesselQuery"
import {
	getUseSeempGetReportsqueryKey,
	useSeempGetReportsQuery,
} from "./queries/useSeempGetReportsQuery"
import SeempNewReportBody from "./SeempNewReportBody"
import SeempReportBody from "./SeempReportBody"
import SeempReportBodyPublished from "./SeempReportBodyPublished"

export type SeempContentProps = { props: AppStructure }

export const getHandlePlantCacheUpdated =
	(queryClient: ReturnType<typeof useQueryClient>) =>
	(data: {
		plantId: number
		updateType: "add" | "update" | "delete"
		data?: SeempAction | SeempReport | Comment
		reportId: number
		actionId?: number
		commentId?: number
	}) => {
		queryClient.setQueryData<SeempReport[] | undefined | null>(
			getUseSeempGetReportsqueryKey(data.plantId),
			(prev) => {
				if (prev === undefined || prev === null) {
					return prev
				}
				switch (data.updateType) {
					case "add":
						if (data.actionId !== undefined) {
							if (data.commentId !== undefined) {
								return prev.map((report) => {
									if (report.id === data.reportId) {
										return {
											...report,
											actions: report.actions.map((action) => {
												if (action.id === data.actionId) {
													return {
														...action,
														comments:
															action.comments !== null
																? [
																		...action.comments.filter(
																			(c) =>
																				c.id !==
																				data.commentId
																		),
																		data.data as Comment,
																	]
																: [data.data as Comment],
													}
												}
												return action
											}),
										}
									}
									return report
								})
							}
							return prev.map((report) => {
								if (report.id === data.reportId) {
									return {
										...report,
										actions: [
											...report.actions.filter(
												(action) => action.id !== data.actionId
											),
											data.data as SeempAction,
										],
									}
								}
								return report
							})
						}
						return [
							...prev.filter((report) => report.id !== data.reportId),
							data.data as SeempReport,
						]

					case "update":
						if (data.actionId !== undefined) {
							if (data.commentId !== undefined) {
								return prev.map((report) => {
									if (report.id === data.reportId) {
										return {
											...report,
											actions: report.actions.map((action) => {
												if (action.id === data.actionId) {
													return {
														...action,
														comments:
															action.comments !== null
																? action.comments.map((comment) => {
																		if (
																			comment.id ===
																			data.commentId
																		) {
																			return data.data as Comment
																		}
																		return comment
																	})
																: null,
													}
												}
												return action
											}),
										}
									}
									return report
								})
							}
							return prev.map((report) => {
								if (report.id === data.reportId) {
									return {
										...report,
										actions: report.actions.map((action) => {
											if (action.id === data.actionId) {
												return data.data as SeempAction
											}
											return action
										}),
									}
								}
								return report
							})
						}
						return prev.map((report) => {
							if (report.id === data.reportId) {
								return data.data as SeempReport
							}
							return report
						})

					case "delete":
						if (data.actionId !== undefined) {
							if (data.commentId !== undefined) {
								return prev.map((report) => {
									if (report.id === data.reportId) {
										return {
											...report,
											actions: report.actions.map((action) => {
												if (action.id === data.actionId) {
													return {
														...action,
														comments:
															action.comments !== null
																? action.comments.filter(
																		(comment) =>
																			comment.id !==
																			data.commentId
																	)
																: null,
													}
												}
												return action
											}),
										}
									}
									return report
								})
							}
							return prev.map((report) => {
								if (report.id === data.reportId) {
									return {
										...report,
										actions: report.actions.filter(
											(action) => action.id !== data.actionId
										),
									}
								}
								return report
							})
						}
						return prev.filter((report) => report.id !== data.reportId)

					default:
						return prev
				}
			}
		)
	}

function ComponentBody({ companyName }: { companyName: string }) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()

	const currentLocation = useLocation().pathname

	const userData = useAppSelector(getUserData)
	const features = common.companyDataFromCompanyName.get(companyName)?.features
	const navigate = useNavigate({
		from: currentLocation.includes("clients") ? "/clients/$clientName/seemp" : "/seemp",
	})

	const match = useMatch({
		from: currentLocation.includes("clients")
			? "/clients/$clientName/_sopPageLayout/seemp"
			: "/_sopPageLayout/seemp",
		shouldThrow: false,
	})

	const paramSelectedVessel = match?.search.vessel ?? ""

	let plantData: PlantObject | undefined
	if (paramSelectedVessel !== "") {
		plantData = common.plantDataFromOwnerAndName.get(
			`${companyName?.toLowerCase()}/${paramSelectedVessel?.toLowerCase()}`
		)
	}

	const {
		data: seempReports,
		isLoading: isSeempReportsLoading,
		isError: isSeempReportsError,
		refetch: refetchSeempReports, // <-- use this function to trigger reload
	} = useSeempGetReportsQuery({
		plantId: plantData?.id,
	})

	const [timeSinceRefetch, setTimeSinceRefetch] = useState<string>("Never refetched")

	const handleRefetchSeempReports = async () => {
		setTimeSinceRefetch("Refetching...")
		await refetchSeempReports()
		setTimeSinceRefetch("Reload")
	}

	const [activeTab, setActiveTab] = useState<string>()
	const [previousTab, setPreviousTab] = useState<string | null>(null)

	const handleTabChange = (newValue: string) => {
		if (activeTab !== undefined) {
			setPreviousTab(activeTab)
		}
		setActiveTab(newValue)
	}

	const tabsListRef = useRef<HTMLDivElement>(null)
	const blurContainerRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (
			seempReports === null ||
			seempReports === undefined ||
			(activeTab !== undefined &&
				seempReports?.map((r) => r.id.toString()).includes(activeTab) === true)
		) {
			return
		}
		setActiveTab(seempReports[0]?.id?.toString() ?? "0")
	}, [seempReports])

	useEffect(() => {
		const scrollHorizontally = (e: WheelEvent) => {
			e.preventDefault()
			if (tabsListRef.current?.scrollWidth === tabsListRef.current?.clientWidth) {
				return
			}
			if (tabsListRef.current !== null) {
				tabsListRef.current.scrollLeft += e.deltaY
			}

			if (tabsListRef.current !== null && blurContainerRef.current !== null) {
				const scrollLeft = tabsListRef.current.scrollLeft
				blurContainerRef.current.style.right = `${0 - scrollLeft}px`
			}
		}
		// Wheel-based horizontal scroll
		tabsListRef.current?.addEventListener("wheel", (e) => scrollHorizontally(e))
		// Drag-to-scroll (mouse)
		let isDown = false
		let startX = 0
		let startScrollLeft = 0
		const onMouseDown = (e: MouseEvent) => {
			isDown = true
			startX = e.pageX - (tabsListRef.current?.offsetLeft ?? 0)
			startScrollLeft = tabsListRef.current?.scrollLeft ?? 0
		}
		const onMouseLeave = () => {
			isDown = false
		}
		const onMouseUp = () => {
			isDown = false
		}
		const onMouseMove = (e: MouseEvent) => {
			if (!isDown || tabsListRef.current === null) return
			e.preventDefault()
			const x = e.pageX - tabsListRef.current.offsetLeft
			const walk = (x - startX) * 1
			tabsListRef.current.scrollLeft = startScrollLeft - walk
		}
		tabsListRef.current?.addEventListener("mousedown", onMouseDown)
		tabsListRef.current?.addEventListener("mouseleave", onMouseLeave)
		tabsListRef.current?.addEventListener("mouseup", onMouseUp)
		tabsListRef.current?.addEventListener("mousemove", onMouseMove)

		return () => {
			tabsListRef.current?.removeEventListener("wheel", (e) => scrollHorizontally(e))
			tabsListRef.current?.removeEventListener("mousedown", onMouseDown)
			tabsListRef.current?.removeEventListener("mouseleave", onMouseLeave)
			tabsListRef.current?.removeEventListener("mouseup", onMouseUp)
			tabsListRef.current?.removeEventListener("mousemove", onMouseMove)
		}
	}, [tabsListRef, blurContainerRef])

	const [seempProgresses, setSeempProgresses] = useState<
		| {
				actionId: number
				progressPercent: number
				result: SeempActionResult | null | undefined
		  }[]
		| undefined
	>()

	useEffect(() => {
		if (companyName === undefined) {
			return
		}
		seempSocket.joinSeempCompanyRoom(companyName)

		const eventManager = new EventSourceManager()

		eventManager.bind(seempSocket2.onSeempActionResultProgress, (data) => {
			const id = data.actionId
			const progressPercent = data.progressPercent
			const result = data.result

			setSeempProgresses((prev = []) => {
				const existingIndex = prev.findIndex((p) => p.actionId === id)
				if (existingIndex !== -1) {
					const updated = [...prev]
					updated[existingIndex] = { actionId: id, progressPercent, result: result }
					return updated
				}
				return [...prev, { actionId: id, progressPercent, result: result }]
			})
		})

		if (plantData === undefined) {
			return
		}

		const handlePlantCacheInvalidated = (data: { plantId: number }) => {
			queryClient.cancelQueries({queryKey: getUseSeempGetReportsqueryKey(data.plantId)})
			queryClient.removeQueries({queryKey: getUseSeempGetReportsqueryKey(data.plantId)})
		}

		const handlePlantCacheUpdated = getHandlePlantCacheUpdated(queryClient)

		seempSocket.joinSeempPlantRoom(companyName, plantData.id)
		seempSocket.onPlantCacheInvalidated(handlePlantCacheInvalidated)
		seempSocket.onPlantCacheUpdated(handlePlantCacheUpdated)

		return () => {
			seempSocket.leaveSeempPlantRoom(companyName, plantData.id, userData.email)
			seempSocket.offPlantCacheInvalidated(handlePlantCacheInvalidated)
			seempSocket.offPlantCacheUpdated(handlePlantCacheUpdated)

			eventManager.clear()
		}
	}, [companyName, plantData])

	useEffect(() => {
		return () => {
			queryClient.cancelQueries({queryKey: getUseSeempGetReportsqueryKey(undefined)})
			queryClient.removeQueries({queryKey: getUseSeempGetReportsqueryKey(undefined)})
		}
	}, [])

	const {
		isLoading: isActionGroupsLoading,
		isError: isActionGroupsError,
		data: actionGroups,
	} = useSeempGetActionsForVesselQuery({ plantId: plantData?.id })

	return (
		<div className={`w-full min-w-fit`}>
			{seempReports !== null &&
			seempReports !== undefined &&
			plantData !== undefined &&
			activeTab !== undefined &&
			actionGroups !== undefined &&
			actionGroups !== null ? (
				<>
					{/* TABS GROUP */}
					<Tabs.Root
						value={activeTab}
						orientation="vertical"
						className=""
						onValueChange={handleTabChange}
					>
						<HeaderLayout
							label={t("SEEMP")}
							customerName={companyName}
						>
							<>
								{/* TABS LIST */}
								<Tabs.List
									ref={tabsListRef}
									className="relative flex w-full flex-row gap-4 overflow-x-hidden pr-3.75"
								>
									{/* REPORT TABS */}
									{seempReports.map((report) => {
										return (
											<Tabs.Trigger
												id={`${report.id}`}
												key={`seempReportTab_${report.id}`}
												value={`${report.id}`}
												className={`mt-1 cursor-default border-b-4 text-left ${
													activeTab === `${report.id}`
														? "border-dipai-primary-501"
														: "border-transparent"
												}`}
											>
												<p
													className={`text-menu col-span-2 truncate px-2 leading-[43px] font-bold ${
														activeTab === `${report.id}`
															? `text-dipai-primary-501`
															: `relative rounded-md text-white/60 hover:cursor-pointer hover:bg-white/10 hover:text-white`
													}`}
													title={report.name}
												>
													{report.name.length > 35
														? report.name.slice(0, 32) + "..."
														: report.name}
												</p>
											</Tabs.Trigger>
										)
									})}
									<Tabs.Trigger
										id={`addNewReport`}
										key={`seempReportTab_addNewReport`}
										value={`addNewReport`}
										className={`mt-1 cursor-default border-b-4 text-left ${
											activeTab === `addNewReport`
												? "border-dipai-primary-501"
												: "border-transparent"
										}`}
										onClick={() => {
											// navigate({
											// 	search: (prev) => ({
											// 		...prev,
											// 		tab: content.name,
											// 	}),
											// })
										}}
									>
										<p
											className={`text-menu col-span-2 truncate px-2 leading-[43px] font-bold ${
												activeTab === `addNewReport`
													? `text-dipai-primary-501`
													: `relative rounded-md text-white/60 hover:cursor-pointer hover:bg-white/10 hover:text-white`
											}`}
										>
											{`+ ${t("NEW REPORT")}`}
										</p>
									</Tabs.Trigger>
									<div
										ref={blurContainerRef}
										className={`to-dipai-secondary-901 pointer-events-none absolute top-0 right-0 h-12.5 w-7.5 bg-linear-to-r from-[rgba(28,43,57,0.3)]`}
									/>
								</Tabs.List>
							</>
						</HeaderLayout>
						{/* COMPONENTS TABS CONTENT */}
						{/* <button onClick={handleRefetchSeempReports}>{t(timeSinceRefetch)}</button> */}
						{seempReports.map((report) => (
							<Tabs.Content
								key={`seempReportContent_${report.id}`}
								value={`${report.id}`}
								className={`p-5`}
							>
								{report.isPublished === true ? (
									<SeempReportBodyPublished
										plantId={plantData.id}
										reportData={report}
										actionGroups={actionGroups}
									/>
								) : (
									<SeempReportBody
										plantId={plantData.id}
										reportData={report}
										actionGroups={actionGroups}
										onRefetchReports={handleRefetchSeempReports}
									/>
								)}
							</Tabs.Content>
						))}
						<Tabs.Content
							key={`seempReportContent_addNewReport`}
							value={`addNewReport`}
							className={`p-5`}
						>
							<SeempNewReportBody
								companyName={companyName}
								plantId={plantData.id}
								lastActiveTab={previousTab}
								setActiveTab={setActiveTab}
								actionGroups={actionGroups}
							/>
						</Tabs.Content>
					</Tabs.Root>
				</>
			) : (
				<div className={`relative h-full w-full`}>
					<HeaderLayout
						label={t("SEEMP")}
						customerName={companyName}
					/>
					<div className={`absolute top-0 left-[calc(50%-30px)]`}>
						<LoadingCircle containerHeightWithUnit="100vh" />
					</div>
				</div>
			)}
		</div>
	)
}

export default function SeempContent({ props }: SeempContentProps) {
	const customerName = Object.keys(props)[0]
	if (customerName === undefined) {
		return <div>{"Internal error: No customer found"}</div>
	}

	return <ComponentBody companyName={customerName} />
}
